import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserUnits, fetchUserDetails } from 'actions/Users';
import { fetchAllSchedules } from 'actions/Settings';
import TabbedSection from "components/TabbedSection";
import Icon from "components/Icon";
import UserSettingsInformationModal from "app/routes/Sites/routes/Display/UnitsUsers/UserSettings/UserSettingsInformationModal";
import UserSettingsSettingsModal from "app/routes/Sites/routes/Display/UnitsUsers/UserSettings/UserSettingsSettingsModal";
// import UserSettingsThemeModal from "app/routes/Sites/routes/Display/UnitsUsers/UserSettings/UserSettingsThemeModal";
import UserSettingsAccessCodeModal from "app/routes/Sites/routes/Display/UnitsUsers/UserSettings/UserSettingsAccessCodeModal";
import UserSettingsFobsModal from "app/routes/Sites/routes/Display/UnitsUsers/UserSettings/UserSettingsFobsModal";
import UserSettingsFacilitiesModal from "app/routes/Sites/routes/Display/UnitsUsers/UserSettings/UserSettingsFacilitiesModal";
import UserSettingsNotificationsModal from "app/routes/Sites/routes/Display/UnitsUsers/UserSettings/UserSettingsNotificationsModal";
import UserSettingsTrainingModal from "app/routes/Sites/routes/Display/UnitsUsers/UserSettings/UserSettingsTrainingModal";
import UserSettingsScheduleModal from "app/routes/Sites/routes/Display/UnitsUsers/UserSettings/UserSettingsScheduleModal";
import UserSettingsReportsModal from "app/routes/Sites/routes/Display/UnitsUsers/UserSettings/UserSettingsReportsModal";
import UserSettingsAuthenticationModal from "app/routes/Sites/routes/Display/UnitsUsers/UserSettings/UserSettingsAuthenticationModal";

const UserSettingsModal = ({ uuDetail }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const siteSchedules = useSelector(state => state.settings.siteSchedules);
  const userUnits = useSelector(state => state.users.userUnits);
  const authUser = useSelector(state => state.auth.authUser);
  const site = useSelector(state => state.auth.site);

  const hasPMS = !!(site?.pms);
  const isTenant = uuDetail.unit.User?.role?.tier === 0;

  useEffect(() => {
    dispatch(fetchAllSchedules());
    dispatch(fetchUserDetails(uuDetail.unit.User?.uuid || uuDetail.unit.User?.id.toString()));
    dispatch(fetchUserUnits(uuDetail.unit.User?.uuid || uuDetail.unit.User?.id.toString()));
  }, [dispatch, uuDetail.unit.User]);

  const tabs = [
    {
      tab: "Information",
      label: intl.formatMessage({ id: "pages.settings.info" }),
      icon: <Icon name="user" />,
      showLabel: false,
      showTooltip: true,
      condition: true, // Always visible
    },
    {
      tab: "Settings",
      label: intl.formatMessage({ id: "pages.settings.Types" }),
      icon: <Icon name="settings" />,
      showLabel: false,
      showTooltip: true,
      condition: authUser?.uuid === uuDetail.unit.User.uuid || authUser?.id === uuDetail.unit.User.id, // Only for own settings
    },
    // hiding theme until we can build this out in new UI
    // {
    //   tab: "Themes",
    //   label: intl.formatMessage({ id: "pages.themes.Type" }),
    //   icon: <Icon name="theme" />,
    //   showLabel: false,
    //   condition: authUser.uuid === uuDetail.unit.User.uuid || authUser.id === uuDetail.unit.User.id, // Only for own settings
    // },
    {
      tab: "Access Code",
      label: intl.formatMessage({ id: "db.accessCode" }),
      icon: <Icon name="accessCodes" />,
      showLabel: false,
      showTooltip: true,
      condition:
        (!hasPMS || (hasPMS && !isTenant)) &&
        authUser?.permissions?.includes('manage_access_codes') // all users if facility has PMS, otherwise only non-tenant users
    },
    {
      tab: "Fobs",
      label: intl.formatMessage({ id: "pages.fobs.Types" }),
      icon: <Icon name="fob" />,
      showLabel: false,
      showTooltip: true,
      condition: authUser?.permissions.includes("user_fobs"), // Requires user_fobs permission
    },
    {
      tab: "Facilities",
      label: intl.formatMessage({ id: "pages.sites.Types" }),
      icon: <Icon name="facilities" />,
      showLabel: false,
      showTooltip: true,
      condition: authUser?.permissions.includes("manage_sites"), // Requires manage_sites permission
    },
    {
      tab: "Notifications",
      label: intl.formatMessage({ id: "pages.notifications.Types" }),
      icon: <Icon name="notification" />,
      showLabel: false,
      showTooltip: true,
      condition:
        (authUser?.uuid === uuDetail.unit.User.uuid || authUser?.id === uuDetail.unit.User.id) || // Own settings
        (authUser?.uuid !== uuDetail.unit.User.uuid &&
          authUser?.permissions.includes("support_update_user_notifications")), // Not own settings but has permission
    },
    {
      tab: "Training",
      label: intl.formatMessage({ id: "pages.users.training" }),
      icon: <Icon name="training" />,
      showLabel: false,
      showTooltip: true,
      condition: authUser?.permissions.includes("support_send_digital_ack"), // Requires support_send_digital_ack permission
    },
    {
      tab: "Schedule",
      label: intl.formatMessage({ id: "pages.schedules.Type" }),
      icon: <Icon name="schedule" />,
      showLabel: false,
      showTooltip: true,
      condition: authUser?.permissions.includes("manage_users") && siteSchedules?.length > 0, // Requires siteSchedules to have items
    },
    {
      tab: "Reports",
      label: intl.formatMessage({ id: "pages.reports.Types" }),
      icon: <Icon name="reports" />,
      showLabel: false,
      showTooltip: true,
      condition: authUser?.permissions.includes("view_suspicious"), // Requires view_suspicious permission
    },
    {
      tab: "Authentication",
      label: intl.formatMessage({ id: "comp.authentication.Header" }),
      icon: <Icon name="authentication" />,
      showLabel: false,
      showTooltip: true,
      condition:
        ((authUser?.uuid === uuDetail.unit.User.uuid || authUser?.id === uuDetail.unit.User.id) &&
          uuDetail.unit.User.role.tier !== 0) || // Own settings and not tier 0
        (authUser?.uuid !== uuDetail.unit.User.uuid &&
          authUser?.permissions.includes("manage_user_2_factor")), // Not own settings but has permission
    },
  ]

  // Filter the tabs based on their conditions
  const visibleTabs = tabs.filter(tab => tab.condition);

  const tabComponents = {
    Information: <UserSettingsInformationModal />,
    Settings: <UserSettingsSettingsModal />,
    // Theme: <UserSettingsThemeModal />,
    "Access Code": <UserSettingsAccessCodeModal />,
    Fobs: <UserSettingsFobsModal />,
    Facilities: <UserSettingsFacilitiesModal />,
    Notifications: <UserSettingsNotificationsModal />,
    Training: <UserSettingsTrainingModal />,
    Schedule: <UserSettingsScheduleModal />,
    Reports: <UserSettingsReportsModal />,
    Authentication: <UserSettingsAuthenticationModal />,
  };

  return (
    <TabbedSection topPadding={false} tabs={visibleTabs} defaultTab="Information">
      {tabComponents}
    </TabbedSection>
  )
};

export default UserSettingsModal;
